<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="900px">
        <v-card>
            <v-card-title>
                AOI Performance KPIs
                
                <v-spacer></v-spacer>
                <v-select
                    v-model="durationSelected"
                    :items="durationOptions"
                    label="Duration"
                    dense
                    hide-details
                    outlined
                    class="mr-4 mt-0"
                >

                </v-select>

                <!-- 1. Toggle for displaying Nets/Groups -->
                <v-switch
                    v-model="showNets"
                    label="Show Groups"
                    dense
                    hide-details
                    class="mr-4 mt-0"
                ></v-switch>

                <v-btn icon @click="$emit('input', false)"><v-icon>{{ icons.mdiClose }}</v-icon></v-btn>
            </v-card-title>
            
            <v-card-text>
                <!-- 2. Added @update:sort-by listener -->
                <v-data-table
                    :headers="kpiHeaders"
                    :items="kpiTableData"
                    dense
                    class="elevation-1"
                    :items-per-page="-1"
                    :custom-sort="sortWithHierarchy"
                    @update:sort-by="handleSortChange"
                    hide-default-footer
                    disable-pagination
                >
                    <template v-slot:item.aoi_name="{ item }">
                        <div :class="{ 
                            'pl-6': item.is_child,          
                            'font-weight-black': item.is_net, 
                            'grey lighten-4': item.is_net   
                        }" class="d-flex align-center fill-height" style="height:100%; width:100%">
                            
                            <!-- NEW: Collapse/Expand Toggle -->
                            <v-btn 
                                v-if="item.is_net" 
                                icon 
                                x-small 
                                class="mr-1"
                                @click.stop="toggleExpand(item.raw_id)"
                            >
                                <v-icon>{{ item.expanded ? icons.mdiChevronDown : icons.mdiChevronRight }}</v-icon>
                            </v-btn>
                            <v-icon v-if="item.is_net" small left color="primary">{{ icons.mdiGroup }}</v-icon>
                            <v-icon v-if="item.is_child" small left color="grey">{{ icons.mdiSubdirectoryArrowRight }}-arrow-right</v-icon>
                            
                            {{ item.aoi_name }}
                        </div>
                    </template>
                    <template v-slot:item.noting="{ item }">
                        <span :class="{'font-weight-bold': item.is_net}">
                            {{ item.noting }}
                        </span>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions v-if="summaryAnalysisData && summaryAnalysisData[durationSelected]">

                <v-card-text class="text--primary">
                    <div>Avg Time on Image: {{ formatValue(summaryAnalysisData[durationSelected].avg_image_duration_sec, 2) }} seconds</div>
                    <div>Avg Number of AOIs see: {{ formatValue(summaryAnalysisData[durationSelected].avg_aoi_count,2) }}</div>
                </v-card-text>
            </v-card-actions>
        </v-card>

        <!-- 3. Snackbar Popup for Sort Warning -->
        <v-snackbar
            v-model="showSortWarning"
            :timeout="5000"
            color="warning"
            class="mb-4"
        >
            Sort unavailable for Groups on this column.
            <template v-slot:action="{ attrs }">
                <v-btn
                    color="white"
                    text
                    v-bind="attrs"
                    @click="disableNetsAndClose"
                >
                    Hide Groups
                </v-btn>
                <v-btn
                    icon
                    v-bind="attrs"
                    @click="showSortWarning = false"
                >
                    <v-icon>{{ icons.mdiClose }}</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

    </v-dialog>    
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import {
    mdiGroup,
    mdiClose,
    mdiSubdirectoryArrowRight,
    mdiChevronRight, 
    mdiChevronDown
} from '@mdi/js'

export default {
  props: ['value', 'mediaType', 'analysisData', 'summaryAnalysisData'],
  setup(props, { emit }) {

    // --- State ---
    const showNets = ref(true);
    const showSortWarning = ref(false);
    const expandedGroups = ref([]); 

    // Columns that don't have data for Nets (triggers the popup)
    const noNetDataColumns = ['order', 'duration_percent', 'avg_rank'];

    const kpiHeaders = computed(() => {
        let headers = [
            { text: 'Order', value: 'order' },
            { text: 'AOI Name', value: 'aoi_name'},
            { text: 'Noting', value: 'noting' },
            { text: 'Visit Duration', value: 'duration'},
            { text: 'Duration %', value: 'duration_percent' },
            { text: 'Time to 1st Fix', value: 'ttff'},
            { text: '1st Element', value: 'noted_first' },
            { text: 'Ranking avg', value: 'avg_rank' },
            { text: 'Coverage', value: 'coverage' },
        ];
        // Filter out specific columns if mediaType != 2 -> Pack
        if (props.mediaType && props.mediaType != 2) {
            const hiddenColumns = ['order', 'avg_rank', 'duration_percent'];
            headers = headers.filter(h => !hiddenColumns.includes(h.value));
        }

        return headers;
    });

    const durationOptions = ref([
        { text: '1 second', value: '1'},
        { text: '2 seconds', value: '2'},
        { text: '3 seconds', value: '3'},
        { text: '4 seconds', value: '4'},
        { text: 'Full', value: 'Full'},
    ])
    const durationSelected = ref('Full')

    const toggleExpand = (id) => {
        const index = expandedGroups.value.indexOf(id);
        if (index === -1) {
            expandedGroups.value.push(id);
        } else {
            expandedGroups.value.splice(index, 1);
        }
    };

    
    const formatValue = (val, decimals = 0) => {
        // If it's the "-" string from backend, return it as is
        if (val === "-" || val === null || typeof val === 'undefined') return "-";
        
        const num = parseFloat(val);
        if (isNaN(num)) return "-";
        
        return num.toFixed(decimals);
    }   

    // --- Data Processing ---
    const kpiTableData = computed(() => {
        if (!props.analysisData) return [];

        const rows = [];
        const processedAOIs = new Set();


        const formatMetrics = (uniqueKey, rawData, isNet = false, isChild = false, rootId = null) => {
           
            const timeSlotData = rawData.time_slots || rawData;
            const metrics = timeSlotData[durationSelected.value] || timeSlotData[durationSelected.value];
            
            if (!metrics) return null;

            const isNum = (v) => v !== '-' && v !== null && typeof v !== 'undefined';

            return {
                id: rootId ? `${rootId}_${uniqueKey}`: uniqueKey,       
                root_id: rootId || uniqueKey,
                raw_id: uniqueKey, 
                aoi_name: rawData.meta.name,
                is_net: isNet,       
                is_child: isChild,   
                
                noting: isNum(metrics.noting) ? formatValue(metrics.noting, 1) + '%' : '-',
                duration: isNum(metrics.duration) ? formatValue(metrics.duration, 0) + 'ms' : '-',
                ttff: isNum(metrics.ttff) ? formatValue(metrics.ttff, 0) + 'ms' : '-',
                noted_first: isNum(metrics.noted_first) ? formatValue(metrics.noted_first, 1) + '%' : '-',
                coverage: isNum(metrics.coverage) ? formatValue(metrics.coverage, 1) + '%' : '-',
                order: isNum(metrics.order) ? formatValue(metrics.order, 0) : '-',
                avg_rank: isNum(metrics.avg_rank) ? formatValue(metrics.avg_rank, 1) : '-',
                duration_percent: isNum(metrics.duration_percent) ? formatValue(metrics.duration_percent, 1) + '%' : '-',
                
                val_aoi_name: rawData.meta.name,
                val_order: isNum(metrics["order"]) ? parseFloat(metrics["order"]) : 999,
                val_noting: isNum(metrics["noting"]) ? parseFloat(metrics["noting"]) : 0,
                val_duration: isNum(metrics["duration"]) ? parseFloat(metrics["duration"]) : 0,
                val_duration_percent: isNum(metrics["duration_percent"]) ? parseFloat(metrics["duration_percent"]) : 0,
                val_ttff: isNum(metrics["ttff"]) ? parseFloat(metrics["ttff"]) : 999999, // High = bottom of list
                val_noted_first: isNum(metrics["noted_first"]) ? parseFloat(metrics["noted_first"]) : 0,
                val_avg_rank: isNum(metrics["avg_rank"]) ? parseFloat(metrics["avg_rank"]) : 999,
                val_coverage: isNum(metrics["coverage"]) ? parseFloat(metrics["coverage"]) : 0,
            };
        };

        // A. Process NETS
        Object.entries(props.analysisData).forEach(([key, val]) => {
            if (val.meta && val.meta.type === 'net') {
                
                // 1. Add Net (Only if global switch is ON)
                if (showNets.value) {
                    const netRow = formatMetrics(key, val, true, false, key);
                    if (netRow) {
                        // Mark if it is expanded
                        netRow.expanded = expandedGroups.value.includes(key);
                        rows.push(netRow);
                    }
                
                    // 2. Add Children (Only if global switch ON **AND** specific group is expanded)
                    if (expandedGroups.value.includes(key) && val.meta.children && val.meta.children.length > 0) {
                        val.meta.children.forEach(childId => {
                            const childData = props.analysisData[childId];
                            if (childData) {
                                const childRow = formatMetrics(childId, childData, false, true, key); 
                                if (childRow) rows.push(childRow);
                                processedAOIs.add(parseInt(childId, 10));
                                processedAOIs.add(childId.toString());
                            }
                        });
                    }
                }
            }
        });

        // B. Process Standalone
        Object.entries(props.analysisData).forEach(([key, val]) => {
            if (key !== 'summary') {
                const isNet = val.meta && val.meta.type === 'net';
                if (!isNet && !processedAOIs.has(key) && !processedAOIs.has(parseInt(key, 10))) {
                    const row = formatMetrics(key, val, false, false, key);
                    if (row) rows.push(row);
                }
            }
        });

        return rows;
    });



    // Triggered when user clicks a header
    const handleSortChange = (val) => {
        // 'val' is the column value (string) or array of strings
        if (!val || (Array.isArray(val) && val.length === 0)) return;
        
        const col = Array.isArray(val) ? val[0] : val;

        // Check if Nets are visible AND the column is one of the 'no-data' columns
        if (showNets.value && noNetDataColumns.includes(col)) {
            showSortWarning.value = true;
        } else {
            showSortWarning.value = false;
        }
    };

    // Action button inside Snackbar
    const disableNetsAndClose = () => {
        showNets.value = false;
        showSortWarning.value = false;
    };


    // --- Sorting Logic ---
    
    const sortWithHierarchy = (items, index, isDesc) => {
        if (index.length === 0 || !items) return items;

        const sortKey = index[0];
        const desc = isDesc[0];
        const rawSortKey = sortKey === 'aoi_name' ? 'aoi_name' : 'val_' + sortKey;

        // MODE 1: Flat Sort (If Nets are hidden)
        // We ignore the hierarchy and just sort everything by value
        if (!showNets.value) {
            return items.sort((a, b) => {
                return compareValues(a[rawSortKey], b[rawSortKey], desc);
            });
        }

        // MODE 2: Hierarchical Sort (If Nets are visible)
        // Group by root -> Sort Groups -> Sort Children
        const groups = {};
        items.forEach(item => {
            const root = item.root_id;
            if (!groups[root]) groups[root] = [];
            groups[root].push(item);
        });

        const sortedGroupKeys = Object.keys(groups).sort((idA, idB) => {
            const parentA = groups[idA].find(x => x.is_net || x.id == idA) || groups[idA][0];
            const parentB = groups[idB].find(x => x.is_net || x.id == idB) || groups[idB][0];

            // If a parent is missing (shouldn't happen here), handle gracefully
            const valA = parentA ? parentA[rawSortKey] : null;
            const valB = parentB ? parentB[rawSortKey] : null;

            return compareValues(valA, valB, desc);
        });

        const sortedResults = [];
        sortedGroupKeys.forEach(rootId => {
            const familyMembers = groups[rootId];
            familyMembers.sort((a, b) => {
                // Net always on top
                if (a.is_net) return -1;
                if (b.is_net) return 1;
                return compareValues(a[rawSortKey], b[rawSortKey], desc);
            });
            sortedResults.push(...familyMembers);
        });

        return sortedResults;
    };

    const compareValues = (a, b, isDesc) => {
        if (a === b) return 0;
        let result = 0;
        
        // Handle nulls always at bottom (or top) depending on preference, 
        // usually for KPIs null means "N/A" so strictly less than 0
        if (a === null || a === undefined) return 1; // push null to end
        if (b === null || b === undefined) return -1; 

        if (typeof a === 'string' && typeof b === 'string') result = a.localeCompare(b);
        else result = a - b;

        return isDesc ? -result : result;
    };

    return {
        kpiHeaders,
        kpiTableData,
        showNets,
        showSortWarning,
        durationSelected,
        durationOptions,
        formatValue,
        sortWithHierarchy,
        handleSortChange,
        disableNetsAndClose,
        toggleExpand,
        expandedGroups,
        icons: {
            mdiGroup,
            mdiClose,
            mdiSubdirectoryArrowRight,
            mdiChevronRight, 
            mdiChevronDown
        }
    }
  }
} 
</script>